import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClientEvent,
  ServerEvent,
  type ClassroomParticipant,
  type ClassroomStatePayload,
  type StrokePreviewBroadcast,
} from '@tutor/realtime';
import type { ChatMessage, WhiteboardPage, WhiteboardStroke } from '@tutor/shared';
import { connectSocket, type ClassroomSocket } from '../lib/socket';

export type ConnectionState = 'connecting' | 'joining' | 'connected' | 'error' | 'ended';

export interface LiveCursor {
  userId: string;
  fullName: string;
  pageId: string;
  x: number;
  y: number;
  at: number;
}

export interface UseClassroomResult {
  connection: ConnectionState;
  error: string | null;
  participants: ClassroomParticipant[];
  pages: WhiteboardPage[];
  activePageId: string | null;
  messages: ChatMessage[];
  typingUsers: string[];
  cursors: LiveCursor[];
  previews: Map<string, StrokePreviewBroadcast>;

  setActivePage: (pageId: string) => void;
  sendStroke: (stroke: Omit<WhiteboardStroke, 'authorId' | 'createdAt'>) => void;
  sendStrokePreview: (preview: Omit<StrokePreviewBroadcast, 'authorId' | 'lessonId'>) => void;
  undo: () => void;
  clearPage: () => void;
  addPage: () => void;
  sendMessage: (body: string) => void;
  setTyping: (isTyping: boolean) => void;
  sendMediaState: (state: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    screenSharing: boolean;
  }) => void;
  endLesson: () => Promise<void>;
}

const CURSOR_TTL_MS = 4_000;

/**
 * Owns the live classroom connection: joins the room, keeps the whiteboard, chat and
 * presence in sync, and exposes the actions the UI can take.
 *
 * All state here is *derived from the server*. Local edits are applied optimistically
 * for the drawer only; every other client's view comes from broadcasts, so the server
 * remains the single source of truth for what is on the board.
 */
export function useClassroom(lessonId: string | undefined, enabled: boolean): UseClassroomResult {
  const socketRef = useRef<ClassroomSocket | null>(null);

  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<ClassroomParticipant[]>([]);
  const [pages, setPages] = useState<WhiteboardPage[]>([]);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typing, setTyping] = useState<Map<string, string>>(new Map());
  const [cursors, setCursors] = useState<Map<string, LiveCursor>>(new Map());
  const [previews, setPreviews] = useState<Map<string, StrokePreviewBroadcast>>(new Map());

  const applyState = useCallback((state: ClassroomStatePayload) => {
    setParticipants(state.participants);
    setPages(state.whiteboard.pages);
    setActivePageId(state.whiteboard.activePageId || (state.whiteboard.pages[0]?.id ?? null));
    setMessages(state.recentMessages);
    setConnection('connected');
    setError(null);
  }, []);

  useEffect(() => {
    if (!lessonId || !enabled) return;

    const socket = connectSocket();
    socketRef.current = socket;
    setConnection(socket.connected ? 'joining' : 'connecting');

    const join = (): void => {
      setConnection('joining');
      socket.emit(ClientEvent.CLASSROOM_JOIN, { lessonId }, (result) => {
        if (result?.ok) {
          // `rtc:ready` is deliberately NOT emitted here — `useWebRTC` announces once
          // its signalling listeners are attached, otherwise the resulting peer-ready
          // burst can arrive before anything is listening for it.
          applyState(result.data);
        } else {
          setConnection('error');
          setError(result?.error.message ?? 'We could not connect you to this classroom.');
        }
      });
    };

    const onConnect = (): void => join();
    const onConnectError = (err: Error): void => {
      setConnection('error');
      setError(
        err.message === 'UNAUTHENTICATED'
          ? 'Your session has expired. Please sign in again.'
          : 'We could not reach the classroom server.',
      );
    };
    const onDisconnect = (): void => setConnection('connecting');

    const onState = (state: ClassroomStatePayload): void => applyState(state);
    const onSync = (state: ClassroomStatePayload): void => applyState(state);

    const onParticipantJoined = ({ participant }: { participant: ClassroomParticipant }): void => {
      setParticipants((current) =>
        current.some((p) => p.userId === participant.userId) ? current : [...current, participant],
      );
    };

    const onParticipantLeft = ({ userId }: { userId: string }): void => {
      setParticipants((current) => current.filter((p) => p.userId !== userId));
      setCursors((current) => {
        const next = new Map(current);
        next.delete(userId);
        return next;
      });
    };

    const onStroke = ({ stroke }: { stroke: WhiteboardStroke }): void => {
      setPages((current) =>
        current.map((page) =>
          page.id === stroke.pageId && !page.strokes.some((s) => s.id === stroke.id)
            ? { ...page, strokes: [...page.strokes, stroke] }
            : page,
        ),
      );
      // The committed stroke supersedes any in-flight preview from that author.
      setPreviews((current) => {
        const next = new Map(current);
        next.delete(stroke.id);
        return next;
      });
    };

    const onPreview = (preview: StrokePreviewBroadcast): void => {
      setPreviews((current) => new Map(current).set(preview.strokeId, preview));
    };

    const onStrokeRemoved = ({ pageId, strokeId }: { pageId: string; strokeId: string }): void => {
      setPages((current) =>
        current.map((page) =>
          page.id === pageId ? { ...page, strokes: page.strokes.filter((s) => s.id !== strokeId) } : page,
        ),
      );
    };

    const onPageCleared = ({ pageId }: { pageId: string }): void => {
      setPages((current) => current.map((page) => (page.id === pageId ? { ...page, strokes: [] } : page)));
    };

    const onPageAdded = ({ page }: { page: WhiteboardPage }): void => {
      setPages((current) => (current.some((p) => p.id === page.id) ? current : [...current, page]));
    };

    const onActivePage = ({ pageId }: { pageId: string }): void => setActivePageId(pageId);

    const onCursor = (cursor: {
      userId: string;
      fullName: string;
      pageId: string;
      x: number;
      y: number;
    }): void => {
      setCursors((current) => new Map(current).set(cursor.userId, { ...cursor, at: Date.now() }));
    };

    const onMessage = (message: ChatMessage): void => {
      setMessages((current) => (current.some((m) => m.id === message.id) ? current : [...current, message]));
    };

    const onTyping = ({ userId, fullName, isTyping }: { userId: string; fullName: string; isTyping: boolean }): void => {
      setTyping((current) => {
        const next = new Map(current);
        if (isTyping) next.set(userId, fullName);
        else next.delete(userId);
        return next;
      });
    };

    const onMediaState = (payload: {
      userId: string;
      audioEnabled: boolean;
      videoEnabled: boolean;
      screenSharing: boolean;
    }): void => {
      setParticipants((current) =>
        current.map((p) =>
          p.userId === payload.userId
            ? {
                ...p,
                media: {
                  audioEnabled: payload.audioEnabled,
                  videoEnabled: payload.videoEnabled,
                  screenSharing: payload.screenSharing,
                },
              }
            : p,
        ),
      );
    };

    const onEnded = (): void => setConnection('ended');
    const onError = ({ message }: { message: string }): void => setError(message);

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);
    socket.on(ServerEvent.CLASSROOM_STATE, onState);
    socket.on(ServerEvent.WHITEBOARD_SYNC, onSync);
    socket.on(ServerEvent.CLASSROOM_PARTICIPANT_JOINED, onParticipantJoined);
    socket.on(ServerEvent.CLASSROOM_PARTICIPANT_LEFT, onParticipantLeft);
    socket.on(ServerEvent.WHITEBOARD_STROKE, onStroke);
    socket.on(ServerEvent.WHITEBOARD_STROKE_PREVIEW, onPreview);
    socket.on(ServerEvent.WHITEBOARD_STROKE_REMOVED, onStrokeRemoved);
    socket.on(ServerEvent.WHITEBOARD_PAGE_CLEARED, onPageCleared);
    socket.on(ServerEvent.WHITEBOARD_PAGE_ADDED, onPageAdded);
    socket.on(ServerEvent.WHITEBOARD_ACTIVE_PAGE, onActivePage);
    socket.on(ServerEvent.WHITEBOARD_CURSOR, onCursor);
    socket.on(ServerEvent.CHAT_MESSAGE, onMessage);
    socket.on(ServerEvent.CHAT_TYPING, onTyping);
    socket.on(ServerEvent.MEDIA_STATE, onMediaState);
    socket.on(ServerEvent.CLASSROOM_ENDED, onEnded);
    socket.on(ServerEvent.ERROR, onError);

    if (socket.connected) join();

    return () => {
      socket.emit(ClientEvent.CLASSROOM_LEAVE, { lessonId });

      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off(ServerEvent.CLASSROOM_STATE, onState);
      socket.off(ServerEvent.WHITEBOARD_SYNC, onSync);
      socket.off(ServerEvent.CLASSROOM_PARTICIPANT_JOINED, onParticipantJoined);
      socket.off(ServerEvent.CLASSROOM_PARTICIPANT_LEFT, onParticipantLeft);
      socket.off(ServerEvent.WHITEBOARD_STROKE, onStroke);
      socket.off(ServerEvent.WHITEBOARD_STROKE_PREVIEW, onPreview);
      socket.off(ServerEvent.WHITEBOARD_STROKE_REMOVED, onStrokeRemoved);
      socket.off(ServerEvent.WHITEBOARD_PAGE_CLEARED, onPageCleared);
      socket.off(ServerEvent.WHITEBOARD_PAGE_ADDED, onPageAdded);
      socket.off(ServerEvent.WHITEBOARD_ACTIVE_PAGE, onActivePage);
      socket.off(ServerEvent.WHITEBOARD_CURSOR, onCursor);
      socket.off(ServerEvent.CHAT_MESSAGE, onMessage);
      socket.off(ServerEvent.CHAT_TYPING, onTyping);
      socket.off(ServerEvent.MEDIA_STATE, onMediaState);
      socket.off(ServerEvent.CLASSROOM_ENDED, onEnded);
      socket.off(ServerEvent.ERROR, onError);
    };
  }, [lessonId, enabled, applyState]);

  // Expire stale cursors so a participant who stops moving does not leave a ghost.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const cutoff = Date.now() - CURSOR_TTL_MS;
      setCursors((current) => {
        let changed = false;
        const next = new Map(current);
        for (const [userId, cursor] of next) {
          if (cursor.at < cutoff) {
            next.delete(userId);
            changed = true;
          }
        }
        return changed ? next : current;
      });
    }, 2_000);

    return () => window.clearInterval(timer);
  }, []);

  const sendStroke = useCallback<UseClassroomResult['sendStroke']>(
    (stroke) => {
      if (!lessonId) return;

      // Applied locally first so the drawer never waits on a network round trip.
      setPages((current) =>
        current.map((page) =>
          page.id === stroke.pageId
            ? {
                ...page,
                strokes: [...page.strokes, { ...stroke, authorId: 'self', createdAt: new Date().toISOString() }],
              }
            : page,
        ),
      );

      socketRef.current?.emit(ClientEvent.WHITEBOARD_STROKE, {
        lessonId,
        stroke: {
          id: stroke.id,
          pageId: stroke.pageId,
          tool: stroke.tool,
          colour: stroke.colour,
          width: stroke.width,
          opacity: stroke.opacity,
          points: stroke.points,
          ...(stroke.data ? { data: stroke.data } : {}),
        },
      });
    },
    [lessonId],
  );

  const sendStrokePreview = useCallback<UseClassroomResult['sendStrokePreview']>(
    (preview) => {
      if (!lessonId) return;
      socketRef.current?.emit(ClientEvent.WHITEBOARD_STROKE_PREVIEW, { lessonId, ...preview });
    },
    [lessonId],
  );

  const undo = useCallback(() => {
    if (!lessonId || !activePageId) return;
    socketRef.current?.emit(ClientEvent.WHITEBOARD_UNDO, { lessonId, pageId: activePageId });
  }, [lessonId, activePageId]);

  const clearPage = useCallback(() => {
    if (!lessonId || !activePageId) return;
    socketRef.current?.emit(ClientEvent.WHITEBOARD_CLEAR_PAGE, { lessonId, pageId: activePageId });
  }, [lessonId, activePageId]);

  const addPage = useCallback(() => {
    if (!lessonId) return;
    socketRef.current?.emit(
      ClientEvent.WHITEBOARD_ADD_PAGE,
      { lessonId, backgroundType: 'GRID' },
      (result) => {
        if (result?.ok) setActivePageId(result.data.page.id);
      },
    );
  }, [lessonId]);

  const setActivePageRemote = useCallback(
    (pageId: string) => {
      setActivePageId(pageId);
      if (lessonId) socketRef.current?.emit(ClientEvent.WHITEBOARD_SET_ACTIVE_PAGE, { lessonId, pageId });
    },
    [lessonId],
  );

  const sendMessage = useCallback(
    (body: string) => {
      if (!lessonId || !body.trim()) return;
      socketRef.current?.emit(ClientEvent.CHAT_SEND, { lessonId, body: body.trim() });
    },
    [lessonId],
  );

  const setTypingRemote = useCallback(
    (isTyping: boolean) => {
      if (!lessonId) return;
      socketRef.current?.emit(ClientEvent.CHAT_TYPING, { lessonId, isTyping });
    },
    [lessonId],
  );

  const sendMediaState = useCallback<UseClassroomResult['sendMediaState']>(
    (state) => {
      if (!lessonId) return;
      socketRef.current?.emit(ClientEvent.MEDIA_STATE, { lessonId, ...state });
    },
    [lessonId],
  );

  const endLesson = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (!lessonId) {
          resolve();
          return;
        }
        socketRef.current?.emit(ClientEvent.CLASSROOM_END, { lessonId }, () => resolve());
      }),
    [lessonId],
  );

  const cursorList = useMemo(() => [...cursors.values()], [cursors]);
  const typingList = useMemo(() => [...typing.values()], [typing]);

  return {
    connection,
    error,
    participants,
    pages,
    activePageId,
    messages,
    typingUsers: typingList,
    cursors: cursorList,
    previews,
    setActivePage: setActivePageRemote,
    sendStroke,
    sendStrokePreview,
    undo,
    clearPage,
    addPage,
    sendMessage,
    setTyping: setTypingRemote,
    sendMediaState,
    endLesson,
  };
}
